import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { trySetServiceUnavailableError } from '../../utils/serviceUnavailableMessages';

const Register = ({ onSwitchToLogin, onRegistrationSuccess }) => {
  const { register } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    agree_to_offer: false,
    agree_to_data_processing: false,
    wechat_id: '',
    telegram_username: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleAboutProjectClick = () => {
    window.history.pushState({}, '', '/landing');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.password_confirm) {
      setError(t('register.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    if (!formData.agree_to_offer) {
      setError(t('register.mustAgreeToOffer'));
      setLoading(false);
      return;
    }

    if (!formData.agree_to_data_processing) {
      setError(t('register.mustAgreeToDataProcessing'));
      setLoading(false);
      return;
    }

    try {
      await register(formData);
      onRegistrationSuccess(formData.email);
    } catch (err) {
      if (trySetServiceUnavailableError(err, setError, t)) return;
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.entries(errorText)
          .map(([field, msgs]) => {
            const fieldName = field.replace(/_/g, ' ');
            const msgList = Array.isArray(msgs) ? msgs : [msgs];
            return msgList.map(m => `${fieldName}: ${m}`);
          })
          .flat();
        setError(messages.join('. '));
      } else {
        setError(t('register.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="auth-card" style={{ maxWidth: '500px', width: '100%' }}>
      <Card.Body className="p-4 p-md-5">
        <div className="text-center mb-4">
          <div className="icon-circle bg-success bg-gradient mx-auto mb-3">
            <i className="bi bi-person-plus text-white"></i>
          </div>
          <h2 className="fw-bold">{t('register.title')}</h2>
          <p className="text-muted">{t('register.subtitle')}</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('register.firstName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder={t('register.firstNamePlaceholder')}
                  autoComplete="given-name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('register.lastName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder={t('register.lastNamePlaceholder')}
                  autoComplete="family-name"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t('register.email')} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('register.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('register.password')} <span className="text-danger">*</span></Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('register.passwordPlaceholder')}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('register.confirmPassword')} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="password"
              name="password_confirm"
              value={formData.password_confirm}
              onChange={handleChange}
              placeholder={t('register.confirmPasswordPlaceholder')}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </Form.Group>

          <p className="text-muted small mb-2">{t('register.messengerHint')}</p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('register.wechatId')}</Form.Label>
                <Form.Control
                  type="text"
                  name="wechat_id"
                  value={formData.wechat_id}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('register.telegramUsername')}</Form.Label>
                <Form.Control
                  type="text"
                  name="telegram_username"
                  value={formData.telegram_username}
                  onChange={handleChange}
                  placeholder="@"
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="agree_to_offer"
              name="agree_to_offer"
              checked={formData.agree_to_offer}
              onChange={handleChange}
              required
              label={
                <span>
                  {t('register.agreeToOffer')}{' '}
                  <a 
                    href="/offer" 
                    onClick={(e) => handleLinkClick(e, '/offer')}
                    className="link-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('register.offerLink')}
                  </a>
                </span>
              }
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Check
              type="checkbox"
              id="agree_to_data_processing"
              name="agree_to_data_processing"
              checked={formData.agree_to_data_processing}
              onChange={handleChange}
              required
              label={t('register.agreeToDataProcessing')}
            />
          </Form.Group>

          <Button
            type="submit"
            className="btn-gradient w-100 py-2"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('register.creating')}
              </>
            ) : (
              t('register.submit')
            )}
          </Button>
        </Form>

        <div className="d-flex gap-2 mt-3">
          <Button
            variant="outline-primary"
            className="flex-grow-1"
            size="lg"
            onClick={handleAboutProjectClick}
          >
            <i className="bi bi-info-circle me-1"></i>
            {t('auth.aboutProject')}
          </Button>
        </div>

        <div className="text-center mt-4 pt-3 border-top">
          <p className="small text-muted mb-2">{t('auth.contactUs')}</p>
          <a
            href="https://t.me/flydarin"
            target="_blank"
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center gap-2 text-decoration-none mb-3"
          >
            <i className="bi bi-telegram" style={{ fontSize: '1.25rem' }}></i>
            <span>{t('auth.telegramChat')}</span>
          </a>
          <div className="mt-2">
            <img src="/images/telegram-qr.png" alt="QR Telegram" width={120} height={120} className="img-fluid" />
          </div>
        </div>

        <hr className="my-4" />

        <p className="text-center mb-0">
          {t('register.haveAccount')}{' '}
          <Button variant="link" className="p-0 link-primary" onClick={onSwitchToLogin}>
            {t('register.signIn')}
          </Button>
        </p>
      </Card.Body>
    </Card>
  );
};

export default Register;
